import React, { ReactElement, useState } from 'react';
import styled from '@emotion/styled';
import {
  OL_COLOUR_KELP,
  OL_COLOUR_BLACK_4_HEX,
  OL_COLOUR_BLACK_90,
  OL_COLOUR_BLACK_55,
} from '../../components/Style/colours';
import { BREAKPOINT_MOBILE } from '../../components/Style/breakpoints';
import { UserSubscriptionPlanSummary } from '../../../ReduxStorage/UserSubscriptionPlans/constants';

import { UsageView } from './UsageView/component';
import { BillingDetails } from './BillingDetails/component';
import { ManageUsers } from './ManageUsers/component';
import { AlertMessage } from '../../components/AlertMessage/component';
import type { SubscriptionPlanSummariesById } from '../../../ReduxStorage/SubscriptionPlans/selectors';
import {
  TabOptions,
  TabOptionTypes,
  UserSubscriptionPlanDetail,
} from './types';
import {
  canCancelPlan,
  canChangePayment,
  makeUserSubscriptionPlanDetail,
} from './helpers';
import type {
  UserSubscriptionBillingPeriodsById,
  UserSubscriptionCumulativeUsageById,
  UserSubscriptionDailyUsageById,
  UserSubscriptionEducatorSeatsById,
  UserSubscriptionLinkedCoursePathsBySubscriptionId,
  UserSubscriptionPaymentSummariesById,
  UserSubscriptionSummaryBySubscriptionId,
  UserSubscriptionPlanNamesById,
  UserSubscriptionLinkedInstitutionPathsBySubscriptionId,
  UserSubscriptionLinkedInstitutionsBySubscriptionId,
} from '../../../ReduxStorage/UserSubscriptionPlans/selectors';
import { UserSummary } from '../../../ReduxStorage/Users/constants';
import { Selector } from '../../components/Forms/Selector/Selector';
import { ManageInstitution } from './ManageInstitution/component';
import { Button } from '../../components/Marketing/Button';
import { CourseMarketplace } from './CourseMarketplace/component';

export interface SubscriptionsAdminProps {
  profileName: string;
  userId: string;
  currentSelectedUserSubscription: UserSubscriptionPlanSummary | null;
  paymentMethodSummaries: UserSubscriptionPaymentSummariesById;
  educatorSeats: UserSubscriptionEducatorSeatsById;
  linkedCoursePaths: UserSubscriptionLinkedCoursePathsBySubscriptionId;
  existingOwnersAndAdmins: UserSubscriptionSummaryBySubscriptionId;
  userSuggestions: Array<UserSummary>;
  dailyUsages: UserSubscriptionDailyUsageById;
  cumulativeUsages: UserSubscriptionCumulativeUsageById;
  subscriptionPlans: SubscriptionPlanSummariesById;
  userSubscriptionPlans: Array<UserSubscriptionPlanSummary>;
  billingPeriods: UserSubscriptionBillingPeriodsById;
  userSubscriptionPlanNames: UserSubscriptionPlanNamesById;
  linkedInstitutionPaths: UserSubscriptionLinkedInstitutionPathsBySubscriptionId;
  linkedInstitutions: UserSubscriptionLinkedInstitutionsBySubscriptionId;
  selectedView: TabOptionTypes;
  hasLoaded: boolean;
  courseMarketplaceEnabled: boolean;
  getUserSuggestion: (params: unknown) => Promise<void>;
  fetchOwnerAndAdmins: () => Promise<void>;
  inviteUserToSubscription: (
    userdId: string,
    subscriptionId: string
  ) => Promise<void>;
  revokeUserAccessToSubscription: (subscriptionRoleId: string) => Promise<void>;
  fetchDailyUsageData: (
    subscriptionId: string,
    fromDate: string,
    toDate: string
  ) => void;
  setCurrentUserSubscriptionPlan: (
    userSubscriptionPlan: UserSubscriptionPlanSummary
  ) => void;
  manageUserSubscriptionPlan: () => void;
  redirectToSolutionsPage: (subscriptionId: string) => void;
  cancelUserSubscriptionPlan: (userSubscriptionPlanId: string) => void;
  updatePaymentMethodClick: (userSubscriptionPlanId: string) => void;
  createInstitutionPortal: (
    institutionName: string,
    institutionPath: string,
    subscriptionId: string
  ) => Promise<void>;
  addAdmin: (institutionId: string, userId: string) => Promise<void>;
  setManageSubscriptionView: (view: TabOptionTypes) => void;
  setUpStripeConnect: (country: string) => Promise<void>;
  goToStripeConnect: () => Promise<void>;
}

const USAGE_LIMIT_EXCEEDED_MESSAGE =
  'Usage limit for your plan have been reached and no new students can enroll. To allow for new enrollments please subscribe to a higher plan or wait until your annual usage resets at the end of the billing period.';
const EDUCATOR_SEAT_LIMIT_EXECEEDED_MESSAGE =
  'Educator limit has been reached. To add more educators to your subscription, please subscribe to a higher plan.';

export const UsageLimitExeededAlertBanner = (): ReactElement => (
  <AlertMessage kind="warning" message={USAGE_LIMIT_EXCEEDED_MESSAGE} />
);
export const EducatorSeatLimitExeededAlertBanner = (): ReactElement => (
  <AlertMessage
    kind="warning"
    message={EDUCATOR_SEAT_LIMIT_EXECEEDED_MESSAGE}
  />
);

const BoldH3 = styled('h3')({
  fontWeight: 'bold',
});

const EmptyStateScene = (): ReactElement => (
  <div>
    <BoldH3>Please select an option</BoldH3>
    <div>Please select an option from the left side pane.</div>
  </div>
);

const StyledLink = styled('a')({
  color: OL_COLOUR_KELP,
});

/* eslint-disable @typescript-eslint/no-use-before-define */
export const SubscriptionsAdmin = React.memo(
  (props: SubscriptionsAdminProps) => {
    const {
      profileName,
      userId,
      currentSelectedUserSubscription,
      educatorSeats,
      linkedCoursePaths,
      userSubscriptionPlans,
      cumulativeUsages,
      subscriptionPlans,
      dailyUsages,
      existingOwnersAndAdmins,
      userSuggestions,
      billingPeriods,
      userSubscriptionPlanNames,
      linkedInstitutionPaths,
      linkedInstitutions,
      selectedView,
      hasLoaded,
      courseMarketplaceEnabled,
      paymentMethodSummaries,
      getUserSuggestion,
      fetchOwnerAndAdmins,
      inviteUserToSubscription,
      revokeUserAccessToSubscription,
      fetchDailyUsageData,
      setCurrentUserSubscriptionPlan,
      manageUserSubscriptionPlan,
      cancelUserSubscriptionPlan,
      updatePaymentMethodClick,
      redirectToSolutionsPage,
      createInstitutionPortal,
      addAdmin,
      setManageSubscriptionView,
      setUpStripeConnect,
      goToStripeConnect,
    } = props;
    const [selectedTab, setSelectedTab] = useState<TabOptionTypes | null>(
      selectedView
    );

    const onSelectTab = (view: TabOptionTypes | null): void => {
      setSelectedTab(view);

      if (view) {
        setManageSubscriptionView(view);
      }
    };

    if (!hasLoaded || userSubscriptionPlans.length === 0) {
      return (
        <EmptyStateWrapper>
          <EmptyStateCard>
            <EmptyStateHeader>
              <Title>Subscriptions</Title>
            </EmptyStateHeader>
            <EmptyStateBody>
              <div>{hasLoaded ? 'You have no subscriptions' : 'Loading..'}</div>
            </EmptyStateBody>
          </EmptyStateCard>
        </EmptyStateWrapper>
      );
    }

    if (!currentSelectedUserSubscription) {
      return null;
    }

    const isOwner = userId === currentSelectedUserSubscription.userId;
    const planCanBeManaged =
      currentSelectedUserSubscription.type === 'personal' ||
      currentSelectedUserSubscription.type === 'portal';

    const getChangePlanAction = (): (() => void) | undefined => {
      if (planCanBeManaged) {
        if (
          currentSelectedUserSubscription.billingMethod === 'braintree' ||
          currentSelectedUserSubscription.billingMethod === 'invoice'
        ) {
          return () =>
            redirectToSolutionsPage(currentSelectedUserSubscription.id);
        } else if (currentSelectedUserSubscription.billingMethod === 'stripe') {
          if (currentSelectedUserSubscription.isActive) {
            return () => manageUserSubscriptionPlan();
          } else {
            return () =>
              redirectToSolutionsPage(currentSelectedUserSubscription.id);
          }
        }
      }

      return undefined;
    };

    const getChangePlan = (): ReactElement | undefined => {
      let action;

      if (action !== undefined) {
        return (
          <Button
            variant="legacy-cta-pink"
            action={{
              kind: 'on_click',
              value: action,
            }}
          >
            Change plan
          </Button>
        );
      }

      return undefined;
    };

    const getChangePaymentMethod = (): ReactElement | undefined => {
      const hasPermission = canChangePayment(currentSelectedUserSubscription);
      let action;

      if (hasPermission) {
        switch (currentSelectedUserSubscription.billingMethod) {
          case 'braintree':
            action = () =>
              updatePaymentMethodClick(currentSelectedUserSubscription.id);
            break;
          case 'stripe':
            action = manageUserSubscriptionPlan;
            break;
          default:
        }
      }

      if (action !== undefined) {
        return (
          <Button
            variant="legacy-primary"
            action={{
              kind: 'on_click',
              value: action,
            }}
          >
            Change payment method
          </Button>
        );
      }

      return undefined;
    };

    const getCancelPlan = (): ReactElement | undefined => {
      if (canCancelPlan(currentSelectedUserSubscription)) {
        return (
          <Button
            variant="legacy-primary"
            action={{
              kind: 'on_click',
              value: () =>
                cancelUserSubscriptionPlan(currentSelectedUserSubscription.id),
            }}
          >
            Cancel subscription
          </Button>
        );
      }

      return undefined;
    };

    const getContactUs = (): ReactElement | undefined => {
      if (currentSelectedUserSubscription.billingMethod === 'invoice') {
        return (
          <StyledLink
            href="https://solutions.openlearning.com/contact"
            target="_blank"
            rel="noreferrer"
          >
            Contact us
          </StyledLink>
        );
      }

      return undefined;
    };

    const getBillingDetailActions = (): ReactElement[] => {
      const actions: ReactElement[] = [];
      const changePlan = getChangePlan();
      const changePaymentMethod = getChangePaymentMethod();
      const cancelPlan = getCancelPlan();
      const contactUs = getContactUs();

      if (changePlan) {
        actions.push(changePlan);
      }
      if (changePaymentMethod) {
        actions.push(changePaymentMethod);
      }
      if (cancelPlan) {
        actions.push(cancelPlan);
      }
      if (contactUs) {
        actions.push(contactUs);
      }

      return actions;
    };

    const SIDE_BAR_ITEMS: TabOptions = [
      {
        key: TabOptionTypes.ACCOUNT_USAGE,
        label: 'Usage',
        onClick: () => onSelectTab(TabOptionTypes.ACCOUNT_USAGE),
        hasPermission: !(
          currentSelectedUserSubscription.billingMethod === 'braintree' &&
          (currentSelectedUserSubscription.type === 'teach' ||
            currentSelectedUserSubscription.type === 'educator')
        ),
      },
      {
        key: TabOptionTypes.BILLING_DETAILS,
        label: 'Billing details',
        hasPermission: true,
        onClick: () => onSelectTab(TabOptionTypes.BILLING_DETAILS),
      },
      {
        key: TabOptionTypes.CHANGE_PLAN,
        label: 'Change Plan',
        onClick: getChangePlanAction(),
        hasPermission: isOwner,
      },
      {
        key: TabOptionTypes.MANAGE_USERS,
        label: 'Manage users',
        hasPermission:
          currentSelectedUserSubscription.type !== 'personal' &&
          currentSelectedUserSubscription.billingMethod !== null,
        onClick: () => onSelectTab(TabOptionTypes.MANAGE_USERS),
      },
      {
        key: TabOptionTypes.MANAGE_INSTITUTION,
        label: 'Manage institution',
        hasPermission:
          currentSelectedUserSubscription.type === 'custom' ||
          currentSelectedUserSubscription.type === 'portal',
        onClick: () => onSelectTab(TabOptionTypes.MANAGE_INSTITUTION),
      },
      {
        key: TabOptionTypes.COURSE_MARKETPLACE,
        label: 'Course marketplace',
        hasPermission: courseMarketplaceEnabled && isOwner,
        onClick: () => onSelectTab(TabOptionTypes.COURSE_MARKETPLACE),
      },
    ];

    let educatorLimit = 0;
    let learnerLimit = 0;
    let portalLimit: number | null = 0;

    const currentSubscriptionPlan =
      subscriptionPlans[currentSelectedUserSubscription.planId];

    if (currentSubscriptionPlan) {
      educatorLimit = currentSubscriptionPlan.educatorLimit || 0;
      learnerLimit = currentSubscriptionPlan.learnerLimit || 0;
      // eslint-disable-next-line prefer-destructuring
      portalLimit = currentSubscriptionPlan.portalLimit;
    }

    const educatorSeatCount =
      educatorSeats[currentSelectedUserSubscription.id] || 0;
    const cumulativeUsageCount: number =
      (cumulativeUsages[currentSelectedUserSubscription.id] &&
        cumulativeUsages[currentSelectedUserSubscription.id].length > 0 &&
        cumulativeUsages[currentSelectedUserSubscription.id]
          .map((usage) => usage.numLearners)
          .reduce((a, b) => a + b)) ||
      0;

    const hasExceededUsageLimit =
      learnerLimit && cumulativeUsageCount >= learnerLimit;
    const hasExceededEducatorSeatLimit =
      educatorLimit && educatorSeatCount >= educatorLimit;

    const currentUserSubscriptionPlanDetail: UserSubscriptionPlanDetail = makeUserSubscriptionPlanDetail(
      currentSelectedUserSubscription,
      subscriptionPlans
    );

    const FILTERED_SIDEBAR_OPTIONS = SIDE_BAR_ITEMS.filter(
      (item) =>
        (item.hasPermission === undefined || item.hasPermission) && item.onClick
    );
    const intitialSelectedTab =
      FILTERED_SIDEBAR_OPTIONS.length > 0
        ? FILTERED_SIDEBAR_OPTIONS[0].key
        : null;

    if (selectedTab === null) {
      onSelectTab(intitialSelectedTab);
    }

    const selectedSideBarItem = SIDE_BAR_ITEMS.find(
      ({ key }) => selectedTab === key
    );

    return (
      <Wrapper>
        <Container>
          <Sidebar>
            <SidebarHeader>
              <SidebarTitle>Manage subscriptions</SidebarTitle>
              <SelectorContainer>
                <Selector
                  options={userSubscriptionPlans.map((plan) => {
                    return {
                      label: userSubscriptionPlanNames[plan.id] || plan.id,
                      value: plan.id,
                    };
                  })}
                  label={
                    userSubscriptionPlanNames[
                      currentSelectedUserSubscription.id
                    ] || currentSelectedUserSubscription.id
                  }
                  name="subscriptions"
                  onChange={(values) => {
                    const selectedUserSubscription = userSubscriptionPlans.find(
                      (plan) => plan.id === values[0]
                    );
                    if (selectedUserSubscription) {
                      setCurrentUserSubscriptionPlan(selectedUserSubscription);
                    }
                  }}
                />
              </SelectorContainer>
            </SidebarHeader>
            <SidebarItemsSection>
              {FILTERED_SIDEBAR_OPTIONS.map((item) =>
                selectedTab === item.key ? (
                  <SelectedSidebarItem
                    key={item.key}
                    role="link"
                    onClick={item.onClick}
                  >
                    {item.label}
                  </SelectedSidebarItem>
                ) : 'onClick' in item ? (
                  <SidebarItem
                    key={item.key}
                    role="link"
                    onClick={item.onClick}
                  >
                    {item.label}
                  </SidebarItem>
                ) : null
              )}
            </SidebarItemsSection>
          </Sidebar>
          <Content>
            {selectedTab === TabOptionTypes.ACCOUNT_USAGE &&
            selectedSideBarItem?.hasPermission ? (
              <UsageView
                isOwner={isOwner}
                UsageLimitExceededBanner={
                  hasExceededUsageLimit ? (
                    <UsageLimitExeededAlertBanner />
                  ) : hasExceededEducatorSeatLimit ? (
                    <EducatorSeatLimitExeededAlertBanner />
                  ) : null
                }
                cumulativeUsages={
                  (cumulativeUsages &&
                    cumulativeUsages[currentSelectedUserSubscription.id]) ||
                  []
                }
                educatorSeats={
                  educatorSeats[currentSelectedUserSubscription.id] || 0
                }
                portalCount={
                  linkedInstitutions[currentSelectedUserSubscription.id]
                    .institutions.length
                }
                educatorLimit={educatorLimit}
                learnerLimit={learnerLimit}
                portalLimit={portalLimit}
                planType={currentSelectedUserSubscription.type}
                dailyUsage={
                  (dailyUsages &&
                    dailyUsages[currentSelectedUserSubscription.id]) ||
                  []
                }
                billingPeriods={
                  (billingPeriods &&
                    billingPeriods[currentSelectedUserSubscription.id]) ||
                  []
                }
                subscriptionId={currentSelectedUserSubscription.id}
                fetchDailyUsageData={fetchDailyUsageData}
                changePlan={getChangePlanAction()}
              />
            ) : selectedTab === TabOptionTypes.BILLING_DETAILS &&
              selectedSideBarItem?.hasPermission ? (
              <BillingDetails
                isOwner={isOwner}
                UsageLimitExceededBanner={
                  planCanBeManaged && hasExceededUsageLimit ? (
                    <UsageLimitExeededAlertBanner />
                  ) : null
                }
                plan={currentUserSubscriptionPlanDetail}
                paymentMethodSummary={
                  (paymentMethodSummaries &&
                    paymentMethodSummaries[
                      currentSelectedUserSubscription.id
                    ]) ||
                  null
                }
                actions={getBillingDetailActions()}
              />
            ) : selectedTab === TabOptionTypes.MANAGE_USERS &&
              selectedSideBarItem?.hasPermission ? (
              <ManageUsers
                userSubscriptionPlan={currentSelectedUserSubscription}
                subscriptionId={currentSelectedUserSubscription.id}
                userSuggestions={userSuggestions}
                existingOwnersAndAdmins={
                  existingOwnersAndAdmins[currentSelectedUserSubscription.id] ||
                  []
                }
                linkedCoursePaths={
                  (linkedCoursePaths &&
                    linkedCoursePaths[currentSelectedUserSubscription.id]) ||
                  []
                }
                linkedInstitutionPaths={
                  (linkedInstitutionPaths &&
                    linkedInstitutionPaths[
                      currentSelectedUserSubscription.id
                    ]) ||
                  []
                }
                getUserSuggestion={getUserSuggestion}
                fetchOwnerAndAdmins={fetchOwnerAndAdmins}
                inviteUserToSubscription={inviteUserToSubscription}
                revokeUserAccessToSubscription={revokeUserAccessToSubscription}
              />
            ) : selectedTab === TabOptionTypes.MANAGE_INSTITUTION &&
              selectedSideBarItem?.hasPermission ? (
              <ManageInstitution
                portalLimit={currentUserSubscriptionPlanDetail.portalLimit}
                planType={currentUserSubscriptionPlanDetail.type}
                currentUserProfileName={profileName}
                institutions={
                  (linkedInstitutions &&
                    linkedInstitutions[currentSelectedUserSubscription.id]
                      .institutions) ||
                  []
                }
                owners={
                  (linkedInstitutions &&
                    linkedInstitutions[currentSelectedUserSubscription.id]
                      .owners) ||
                  []
                }
                createInstitutionPortal={(institutionName, institutionPath) =>
                  createInstitutionPortal(
                    institutionName,
                    institutionPath,
                    currentSelectedUserSubscription.id
                  )
                }
                addAdmin={(institutionId: string) =>
                  addAdmin(institutionId, userId)
                }
              />
            ) : selectedTab === TabOptionTypes.COURSE_MARKETPLACE &&
              courseMarketplaceEnabled &&
              selectedSideBarItem?.hasPermission ? (
              <CourseMarketplace
                revenueShare={currentUserSubscriptionPlanDetail.revenueShare}
                planType={currentUserSubscriptionPlanDetail.type}
                accountStatus={
                  currentUserSubscriptionPlanDetail.stripeConnectStatus
                }
                goToStripeConnect={goToStripeConnect}
                onSetupStripeAccountClick={setUpStripeConnect}
              />
            ) : (
              <EmptyStateScene />
            )}
          </Content>
        </Container>
      </Wrapper>
    );
  }
);

/* eslint-enable @typescript-eslint/no-use-before-define */

const BORDER_STYLE = '1px solid #CACACA';
const MOBILE_MEDIA_QUERY = `@media (max-width: ${BREAKPOINT_MOBILE} )`;

const Wrapper = styled('div')({
  background: OL_COLOUR_BLACK_4_HEX,
  padding: '14px 32px',

  [MOBILE_MEDIA_QUERY]: {
    padding: '8px',
  },
});

const Container = styled('div')({
  maxWidth: '90em',
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'flex',
  width: '100%',
  border: BORDER_STYLE,
  borderRadius: 4,

  [MOBILE_MEDIA_QUERY]: {
    flexDirection: 'column',
  },
});

const Content = styled('div')({
  flexGrow: 1,
  padding: '24px 40px',
  background: '#ffffff',

  [MOBILE_MEDIA_QUERY]: {
    padding: '12px',
    overflow: 'auto',
  },
});

const Sidebar = styled('div')({
  width: 237,
  minWidth: 237,
  borderRight: BORDER_STYLE,
  minHeight: 210,
  background: OL_COLOUR_BLACK_4_HEX,

  [MOBILE_MEDIA_QUERY]: {
    width: '100%',
    borderRight: 'none',
    borderBottom: BORDER_STYLE,
  },
});

const SidebarHeader = styled('div')({
  background: '#EDEDED',
  width: '100%',
  margin: 0,
  borderBottom: BORDER_STYLE,
  padding: '16px 13px',
});

const SidebarTitle = styled('h2')({
  background: '#EDEDED',
  color: OL_COLOUR_KELP,
  fontSize: 16,
  fontWeight: 600,
  lineHeight: 1.375,
});

const SidebarItemsSection = styled('div')({
  width: '100%',
  paddingTop: 12,
});

const sidebarItemStyles = {
  cursor: 'pointer',
  display: 'block',
  fontSize: 14,
  lineHeight: 1.375,
  marginBottom: 10,
  padding: '8px 0 8px 44px',
  fontWeight: 600,
  color: OL_COLOUR_BLACK_90,

  '&:hover': {
    background: '#8D8D8D',
    color: '#ffffff',
    textDecoration: 'none',
  },
};

const selectedItemStyle = {
  background: '#8D8D8D',
  color: '#ffffff',
};

const SidebarItem = styled('div')(sidebarItemStyles);
const SelectedSidebarItem = styled('div')([
  sidebarItemStyles,
  selectedItemStyle,
]);

const SelectorContainer = styled('div')({
  border: `solid 1px ${OL_COLOUR_BLACK_55}`,
  borderRadius: 4,

  '.btn-link': {
    color: OL_COLOUR_BLACK_55,
  },

  '.olselector .olselector-button::after': {
    borderTopColor: OL_COLOUR_BLACK_55,
  },
});

const EmptyStateWrapper = styled('div')({
  maxWidth: '80em',
  margin: '0 auto',
  padding: '4em 0',
});

const EmptyStateHeader = styled('div')({
  display: 'flex',
  borderBottom: `1px solid ${OL_COLOUR_BLACK_55}`,
});

const EmptyStateBody = styled('div')({
  padding: 30,
  fontSize: 14,
  lineHeight: 1.5,
});

const Title = styled('h3')({
  color: OL_COLOUR_BLACK_55,
  fontSize: '1.8em',
  fontWeight: 600,
  paddingLeft: 30,
  paddingRight: 30,
});

const EmptyStateCard = styled('div')({
  background: '#fff',
  boxShadow: '0 1px 10px rgba(0, 0, 0, 0.2)',
});
